import { FieldSet, Form } from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import logger from '@redbubble/next/logger';
import { difference, isEqual, omit, without } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SizeGuideButton } from '../../../../components/SizeGuide';
import { InventoryItemGaCode } from '../../../../types/shop-bff/types';
import { AttributeFieldDelegator } from './AttributeFieldDelegator';
import { delegateField } from './fieldDelegationUtils';
import {
  AttributeName,
  type ConfiguratorField,
  type ConfiguratorValues,
  DSFormChangeType,
  DSFormOnChangeCallback,
  type FormikValues,
} from './types';
import { useConfiguratorViewportProfile } from './useConfiguratorViewportProfile';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import {
  configuratorValuesToFormikValues,
  getFallbackDefaultText,
} from './utils';

type HiddenAttributes = AttributeName[];

export const CONFIGURATOR_FORM_ID = 'product-configurator-form';

export interface ProductConfiguratorProps {
  currentValues: ConfiguratorValues;
  onChange: DSFormOnChangeCallback;
  configuratorFields: Array<ConfiguratorField>;
  onSubmit: () => Promise<void>;
  fieldsRequiringATCGuard: string[];
  analyticsCode: InventoryItemGaCode;
  currentProductTypeId: string;
  currentWorkId: number;
  showSizeGuideButton: boolean;
  hasSizeGuide?: boolean;
  /*
    Because the form field validation requires a form submit action from a submit button,
    and because the configurator does not include a default submit button
    we need to be able to define a submit button elsewhere, and link the the submit button and the form
    together with this formId prop and a "form={formId}" attribute on an external submit button
  */
  formId?: string;
  hiddenAttributes?: HiddenAttributes;
}

const useScrollValidationErrorsIntoView = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // find first error key
    const errorKey = Object.keys(validationErrors).find(Boolean);

    const fieldSetComponent =
      errorKey &&
      document.getElementById(`${CONFIGURATOR_FORM_ID}_${errorKey}`);

    if (!fieldSetComponent) return;

    fieldSetComponent.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }, [prefersReducedMotion, validationErrors]);

  return setValidationErrors;
};

export const ProductConfigurator: React.FC<ProductConfiguratorProps> = ({
  currentValues,
  onChange,
  onSubmit,
  configuratorFields,
  fieldsRequiringATCGuard: initialFieldsRequiringATCGuard,
  analyticsCode,
  currentProductTypeId,
  currentWorkId,
  showSizeGuideButton,
  hasSizeGuide,
  formId,
  hiddenAttributes,
}) => {
  const viewportProfile = useConfiguratorViewportProfile();

  const [fieldsRequiringATCGuard, setFieldsRequiringATCGuard] = useState(
    initialFieldsRequiringATCGuard
  );

  const defaultValues = useMemo(
    () => configuratorValuesToFormikValues(currentValues),
    [currentValues]
  );

  const initialFormValues = useMemo(() => {
    const modifiedDefaultValues = hiddenAttributes
      ? omit(defaultValues, hiddenAttributes)
      : { ...defaultValues };
    return omit(modifiedDefaultValues, fieldsRequiringATCGuard);
  }, [defaultValues, fieldsRequiringATCGuard, hiddenAttributes]);

  const handleFormChange = (
    formState: DSFormChangeType,
    prevFormState: DSFormChangeType
  ) => {
    if (isEqual(formState.values, prevFormState.values)) return;

    const atcFieldsChanged = Object.keys(formState.values).filter((key) =>
      fieldsRequiringATCGuard.includes(key)
    );

    if (atcFieldsChanged.length > 0) {
      setFieldsRequiringATCGuard((prev) => without(prev, ...atcFieldsChanged));
    }

    /**
     * Non-required field should have a default value set.
     * If form values change from not being set, to being set,
     * we exclude it from analytics (for example when form re-renders)
     */
    const changedKeys = difference(
      Object.keys(formState.values),
      Object.keys(prevFormState.values)
    );

    const excludeFromAnalytics = changedKeys.some(
      (changedKey) => !fieldsRequiringATCGuard.includes(changedKey)
    );

    onChange(formState, prevFormState, excludeFromAnalytics);
  };

  const getActionSlot = useCallback(
    (name: string) => {
      if (
        hasSizeGuide &&
        (name === AttributeName.Size || name === AttributeName.CardSize)
      ) {
        return (
          <ErrorBoundary>
            <SizeGuideButton
              productTypeId={currentProductTypeId}
              workId={currentWorkId}
            />
          </ErrorBoundary>
        );
      }
      return null;
    },
    [currentProductTypeId, currentWorkId, hasSizeGuide]
  );

  const setValidationErrors = useScrollValidationErrorsIntoView();

  return (
    <Form
      enableReinitialize
      formId={formId}
      initialValues={initialFormValues}
      onChange={handleFormChange}
      onSubmit={(values, { setSubmitting }) => {
        return onSubmit().then(() => setSubmitting(false));
      }}
      profile={viewportProfile}
      validate={(values: FormikValues): Record<string, string> => {
        const errors: Record<string, string> = {};

        fieldsRequiringATCGuard.forEach((fieldRequiringATCGuard) => {
          if (!values[fieldRequiringATCGuard]) {
            errors[fieldRequiringATCGuard] = 'Required';
          }
        });

        setValidationErrors(errors);

        return errors;
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnSubmit
    >
      {({ errors }) => (
        <>
          {configuratorFields.map((field) => {
            const attributeName = field.name;
            const defaultText = getFallbackDefaultText({
              attributeName,
              fallback: attributeName,
              defaultText: field.defaultText,
            });

            const fieldData = delegateField(field.options, (option) =>
              logger.warn(
                {
                  option,
                  field,
                  message: 'inventory item attribute has missing default text',
                },
                'inventory item attribute has missing default text'
              )
            );

            if (!fieldData.success) {
              const err = { message: 'a configurator error has occurred' };
              logger.error(
                { err, reason: fieldData.reason, field, message: err.message },
                err.message
              );
              return null;
            }

            const ActionComponent = showSizeGuideButton
              ? getActionSlot(attributeName)
              : undefined;

            if (
              hiddenAttributes &&
              hiddenAttributes.some((attr) => attr === attributeName)
            )
              return null;

            return (
              <FieldSet
                key={attributeName}
                data-test-attribute-name={attributeName}
                data-testid="product-configurator-item"
                id={`${CONFIGURATOR_FORM_ID}_${attributeName}`}
              >
                <AttributeFieldDelegator
                  defaultText={defaultText}
                  attributeName={attributeName}
                  fieldData={fieldData.value}
                  currentValue={defaultValues[attributeName]}
                  errors={errors}
                  actionSlot={ActionComponent}
                  currentProductTypeId={currentProductTypeId}
                />
              </FieldSet>
            );
          })}
        </>
      )}
    </Form>
  );
};
